'use client';

import { forwardRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  PAPER_BAND,
  PAPER_INK,
  PAPER_RULE,
  PAPER_VARIANT,
  PAPER_ACCENT_INK,
} from '@/features/marketing/ui/paper-tokens';
import { LANDING_PRIMARY_CTA_RECIPE } from '@/features/marketing/ui/cta-recipe';

/**
 * WhatsAppDemoFormStaged.tsx — the two UI atoms unique to the staged 3-step
 * flow (R5-P3 LANE L5, design amendment §6 "BOT — staged interactive flow").
 * Split out of WhatsAppDemoForm.tsx to keep that file under the 800-line
 * component cap (`check:code-quality`) — pure presentational, no state/
 * fetch/validation of their own; the parent owns all behavior and passes it
 * in as props.
 *
 * Related: WhatsAppDemoForm.tsx
 */

interface StagedCtaCardProps {
  /** Step 0's headline — the ONLY explainer copy that survives (§6). */
  headline: string;
  /** Step 0's one supporting line. */
  line: string;
  onPress: () => void;
}

/**
 * Step 0 — big "Testează-l și tu" CTA card. The whole card is one button;
 * pressing it advances to the name+phone step.
 *
 * Font: in-card CTA text stays Inter (interface register) — the same
 * resolved ruling this component's own success H3 already uses ("WhatsApp
 * form success H3 ... transient UI microcopy, not a page headline",
 * r5-defect-map.md FONT-USAGE MAP) applies identically here.
 */
export const StagedCtaCard = forwardRef<HTMLButtonElement, StagedCtaCardProps>(
  function StagedCtaCard({ headline, line, onPress }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        data-testid="staged-cta-button"
        // v2 bio-link engagement pipeline (contract 3): this button is the
        // WhatsApp demo's "open" action — pressing it advances step 0 -> 1.
        data-track="cta_demo_open"
        onClick={onPress}
        className={cn(
          'group flex w-full flex-col items-start gap-1.5 rounded-xl border p-5 text-left sm:p-6',
          'transition-colors motion-reduce:transition-none',
          PAPER_RULE.hairline.border,
          PAPER_BAND.base.bg,
          // R7 paper flip: amber hover wash is unchanged (decision amber is
          // legal on any surface) — only the resting border/fill left the
          // dark system above.
          'hover:border-[#F59E0B4D] hover:bg-[#F59E0B0F]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FBBF24]',
          'focus-visible:ring-offset-2',
          PAPER_VARIANT.baseRingOffset
        )}
      >
        <span className="flex w-full items-center justify-between gap-3">
          <span className={cn('text-xl font-semibold sm:text-2xl', PAPER_INK.primary.text)}>
            {headline}
          </span>
          {/* 2026-07-18 slop-audit revision pass FIX 1 (CRITICAL, amber-on-
              paper contrast): was `text-[#FBBF24]`, under WCAG AA's 4.5:1
              floor against this card's paper fill — swapped for
              `PAPER_ACCENT_INK` (contrast-proven). */}
          <ArrowRight
            className={cn(
              'h-5 w-5 shrink-0 transition-transform motion-reduce:transition-none group-hover:translate-x-0.5',
              PAPER_ACCENT_INK.text
            )}
            aria-hidden="true"
          />
        </span>
        <span className={cn('text-sm', PAPER_INK.muted.text)}>{line}</span>
      </button>
    );
  }
);

interface StagedContinueButtonProps {
  label: string;
  onPress: () => void;
}

/** Step 1 -> step 2 gate ("Continuă"). Same primary-CTA recipe as the final
 * submit button, one step earlier in the flow. */
export function StagedContinueButton({ label, onPress }: StagedContinueButtonProps) {
  return (
    <button
      type="button"
      data-testid="staged-continue-button"
      onClick={onPress}
      className={cn(
        LANDING_PRIMARY_CTA_RECIPE,
        'w-full h-12 px-6 py-2.5 rounded-xl',
        'inline-flex items-center justify-center gap-2 font-semibold text-base'
      )}
    >
      <span>{label}</span>
      <ArrowRight className="w-4 h-4 shrink-0" aria-hidden="true" />
    </button>
  );
}
